import { Box, Flex } from '@modusign/design-system';
import { useSigners } from '@modusign/react-signing';
import { SignerToReference } from '@modusign/react-signing/types';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMount } from 'react-use';
import { v1, v4 } from 'uuid';

import CreateTemplateLocalFileUploadModal from '@app/containers/CreateTemplateLocalFileUploadModal';
import embeddedService from '@app/libs/embedded/embeddedService';

import TemplateHeader from '../TemplateHeader';
import TemplateSignerCards from '../TemplateSignerCards';
import TemplateSignerSetupFooter from '../TemplateSignerSetupFooter';

// TODO(@DevSDK, @Juyeong-Byeon): 다른 도메인 참여자 설정 UI 코드중복 확인하기
interface TemplateSignerSetupStepProps {
  isEdition?: boolean;
  editTemplatePermission?: boolean;
  onNextStep: () => void;
  onBackStep: () => void;
}

export function TemplateSignerSetupStep({
  editTemplatePermission,
  isEdition,
  onBackStep,
  onNextStep,
}: TemplateSignerSetupStepProps) {
  const { signers, changeSigners } = useSigners();
  const signerIdsFromSigners = isEdition || signers.length > 0 ? signers.map((p) => p.id) : [v1()];
  const [signerIds, setSignerIds] = useState<string[]>(signerIdsFromSigners);
  const [isInitialized, setIsInitialized] = useState(false);

  const formMethods = useForm<Record<string, SignerToReference>>({
    mode: 'onChange',
  });

  const {
    formState: { isValid },
    reset,
    handleSubmit,
  } = formMethods;

  useMount(() => {
    reset(
      signers.reduce(
        (acc, p): Record<string, SignerToReference> => ({
          ...acc,
          [p.id]: {
            ...p,
            host: p.host
              ? {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  participantId: p.host.id!,
                }
              : undefined,
          },
        }),
        {}
      )
    );
    setIsInitialized(true);
  });

  const saveSignerData = handleSubmit((data) => {
    changeSigners(() => {
      return signerIds.map<SignerToReference>((id) => ({
        ...data[id],
        host: data[id].host,
        dataLabel: data[id].dataLabel ? data[id].dataLabel : v4(),
        id,
        type: 'SIGNER',
      }));
    });
  });

  const handleNextStep = async () => {
    await saveSignerData();
    onNextStep();
  };

  const handleBackStep = async () => {
    await saveSignerData();
    onBackStep();
  };

  return (
    // TODO (#4375): 레이아웃 시스템 변경 논의/변경 필요
    <Flex flexDirection="column" height="100%">
      <TemplateHeader
        currentStepIndex={1}
        isExchangeDocumentVisible={isEdition ? editTemplatePermission : true}
        progressStep="templateParticipantSetup"
      />
      <Box
        className="
          EditorContentContainer--only-desktop-view"
        flex="1"
        minHeight="0"
        overflowY="auto"
      >
        <Box maxWidth="xs" mx="auto" pt={4} px={4}>
          <FormProvider {...formMethods}>
            {isInitialized && (
              <TemplateSignerCards signerIds={signerIds} onSignerIdsChange={setSignerIds} />
            )}
          </FormProvider>
        </Box>
      </Box>
      <TemplateSignerSetupFooter
        backButtonText="< 나가기"
        disableButton={!isValid}
        nextButtonText="다음 단계로 >"
        onBackStepClick={handleBackStep}
        onNextStepClick={handleNextStep}
      />
      {embeddedService.isEmbedded && <CreateTemplateLocalFileUploadModal />}
    </Flex>
  );
}
